import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const PatientConsentForm = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(true);
  const [consentHistory, setConsentHistory] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");

  const positions = [
    "RN",
    "PT",
    "PTA",
    "OT",
    "OTA",
    "ST",
    "HHA",
    "LVN",
    "MSW",
    "Caregiver",
    "CNA",
    "Office Assistant",
    "ADMIN"
  ];

  const [consentFormData, setConsentFormData] = useState({
    employeeId: "",
    employeeName: "",
    // Patient Information
    patientName: "",
    patientDateOfBirth: "",
    patientAddress: "",
    patientPhone: "",
    consentDate: "",
    // Consent Sections (checkboxes)
    consentAdmission: false,
    consentReleaseInfo: false,
    consentCertification: false,
    consentBilling: false,
    consentInsurance: false,
    consentCMSAdvance: false,
    cmsPrivateInsurance: false,
    cmsSelfPay: false,
    consentPhotoRecords: false,
    // Visit Frequency
    visitFrequencySN: "",
    visitFrequencyCHHA: "",
    visitFrequencyPT: "",
    visitFrequencyOT: "",
    visitFrequencyST: "",
    visitFrequencyMSW: "",
    // Certification checkboxes
    certPatientRights: false,
    certOasisPrivacy: false,
    certHotlineInfo: false,
    certPrivacyRights: false,
    certPlanOfCare: false,
    certRates: false,
    certTollFree: false,
    certEmergencyPlan: false,
    certBrochure: false,
    certDocumentCopy: false,
    certAdvanceDirectives: false,
    advanceDirectivesYes: false,
    advanceDirectivesNo: false,
    // Signatures
    patientSignature: "",
    patientNamePrinted: "",
    signatureDate: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      // Exclude file data - we only need user list for selection
      const data = await getUsers(true);
      setUsers(data);
      setLoading(false);
    };
    loadUsers();

    // Load consent history from localStorage
    const savedHistory = localStorage.getItem("consentHistory");
    if (savedHistory) {
      setConsentHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    let fieldName = name || id;

    // If position is selected
    if (id === "selectedPosition") {
      setSelectedPosition(value);
      setConsentFormData(prev => ({
        ...prev,
        employeeId: "",
        employeeName: ""
      }));
      return;
    }

    // If employee is selected, auto-populate name
    if (id === "employeeId") {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        const fullName = `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
        setConsentFormData(prev => ({
          ...prev,
          employeeId: value,
          employeeName: fullName
        }));
        return;
      }
    }

    setConsentFormData(prev => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value
    }));
  };

  const formatDateUS = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "";
    }
  };

  const generateConsentPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient Consent - ${data.patientName || "Patient"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 7.5pt;
            line-height: 1.35;
            padding: 10px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #2c3e50;
            min-height: 80px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 18px;
          }
          .header h1 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 7.5pt;
            text-align: right;
            line-height: 1.3;
          }
          .consent-section {
            margin-bottom: 10px;
            font-size: 7.5pt;
            line-height: 1.35;
          }
          .consent-checkbox {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 1px solid #333;
            margin-right: 6px;
            vertical-align: middle;
            position: relative;
          }
          .consent-checkbox.checked::after {
            content: "✓";
            position: absolute;
            top: -2px;
            left: 1px;
            font-size: 10px;
            color: #333;
          }
          .consent-title {
            font-weight: bold;
            margin-bottom: 6px;
            color: #2c3e50;
          }
          .consent-text {
            margin-left: 20px;
            margin-bottom: 6px;
            text-align: justify;
            padding-right: 10px;
          }
          .visit-frequency {
            display: flex;
            gap: 15px;
            margin: 8px 0;
            font-size: 7.5pt;
            flex-wrap: wrap;
          }
          .visit-item {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .visit-label {
            font-weight: 600;
            color: #495057;
            white-space: nowrap;
          }
          .visit-value {
            border-bottom: 1px solid #333;
            min-width: 100px;
            min-height: 18px;
            padding-bottom: 3px;
          }
          .certification-list {
            margin-left: 20px;
            margin-top: 6px;
            margin-bottom: 6px;
          }
          .cert-item {
            margin-bottom: 4px;
            line-height: 1.35;
            font-size: 7pt;
          }
          .signature-section {
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid #333;
          }
          .signature-row {
            display: flex;
            gap: 25px;
            margin-bottom: 10px;
            font-size: 7.5pt;
          }
          .signature-field {
            flex: 1;
          }
          .signature-field.half {
            flex: 0 0 calc(50% - 10px);
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
            font-size: 7.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #333;
            min-height: 18px;
            padding-bottom: 3px;
            color: #212529;
          }
          .signature-note {
            font-size: 7pt;
            font-style: italic;
            color: #666;
            margin-top: 4px;
          }
          .checkbox-inline {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            margin-right: 12px;
            font-size: 7.5pt;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 10px;
            height: 10px;
            margin: 0;
            vertical-align: middle;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div><strong>Infinite Home Health, Inc.</strong></div>
            <div>22151 Ventura Blvd, #201</div>
            <div>Woodland Hills, CA 91364</div>
            <div>Tel: (818) 888-7772</div>
            <div>Fax: (818) 888-7773</div>
          </div>
          <div class="header-content">
            <h1>Patient Consent</h1>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentAdmission ? "checked" : ""}"></span>
            REQUEST FOR ADMISSION AND CONSENT TO TREATMENT
          </div>
          <div class="consent-text">
            I consent to initial evaluation for eligibility and admission to services and treatment as ordered by my physician through Infinite Home Health. I understand that my care is directed and monitored by my physician and that Infinite Home Health is not liable for any acts or omissions when following the instructions of my physician, as physicians are neither employees nor agents of Infinite Home Health.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentReleaseInfo ? "checked" : ""}"></span>
            AUTHORIZATION TO RELEASE INFORMATION
          </div>
          <div class="consent-text">
            I consent to the release of information from any hospital, skilled nursing facility, or home care agency where I have been a patient. I authorize health care professionals to disclose medical records to Infinite Home Health and authorize Infinite Home Health to share health status information with other health agencies, professionals involved in my care, or State, Federal, and other accrediting bodies for record review. I understand that I may request transfer to another HHA at any time.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentCertification ? "checked" : ""}"></span>
            PATIENTS CERTIFICATION, AUTHORIZATION TO RELEASE INFORMATION, AND PAYMENT REQUEST
          </div>
          <div class="consent-text">
            I certify that the information given to Infinite Home Health for payment from CMS is true and correct. I authorize the release of all medical records allowed under HIPAA regulations and authorize payment on my behalf to Infinite Home Health.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentBilling ? "checked" : ""}"></span>
            HOME HEALTH CONSOLIDATED BILLING UNDER PPS
          </div>
          <div class="consent-text">
            I understand that services subject to home health consolidated billing include SN care, CHHA, PT, ST, OT, MSW routine and non-routine medical supplies, and care for homebound patients involving cumbersome equipment. DME is excluded. I was advised in advance by the HHA about consolidated billing expectations from Medicare or other sources, including myself.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentInsurance ? "checked" : ""}"></span>
            ASSIGNMENT OF INSURANCE BENEFITS
          </div>
          <div class="consent-text">
            I authorize direct payment of insurance benefits to Infinite Home Health for services at a rate not exceeding regular charges. I understand that I am responsible for charges not covered by my insurance plan. Medicare covers all Home Health billable expenses.
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentCMSAdvance ? "checked" : ""}"></span>
            CMS ADVANCE BENEFICIARY NOTICE
          </div>
          <div class="consent-text">
            I agree to individually obligate myself to pay Infinite Home Health for services according to regular rates and terms. If this account is referred to an attorney for collection, I will pay reasonable collection expenses, including attorney fees.
          </div>
          <div style="margin-left: 20px; margin-top: 4px;">
            <span class="checkbox-inline">
              <input type="checkbox" ${data.cmsPrivateInsurance ? "checked" : ""} disabled>
              <span style="font-weight: bold;">PRIVATE INSURANCE</span>
            </span>
            <span class="checkbox-inline">
              <input type="checkbox" ${data.cmsSelfPay ? "checked" : ""} disabled>
              <span style="font-weight: bold;">SELF PAY</span>
            </span>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title" style="margin-bottom: 6px;">Visit Frequency:</div>
          <div class="visit-frequency">
            <div class="visit-item">
              <span class="visit-label">SN Visits:</span>
              <span class="visit-value">${data.visitFrequencySN || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">CHHA Visits:</span>
              <span class="visit-value">${data.visitFrequencyCHHA || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">PT:</span>
              <span class="visit-value">${data.visitFrequencyPT || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">OT:</span>
              <span class="visit-value">${data.visitFrequencyOT || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">ST:</span>
              <span class="visit-value">${data.visitFrequencyST || "________________"}</span>
            </div>
            <div class="visit-item">
              <span class="visit-label">MSW:</span>
              <span class="visit-value">${data.visitFrequencyMSW || "________________"}</span>
            </div>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.certPatientRights || data.certOasisPrivacy || data.certHotlineInfo || data.certPrivacyRights || data.certPlanOfCare || data.certRates || data.certTollFree || data.certEmergencyPlan || data.certBrochure || data.certDocumentCopy || data.certAdvanceDirectives ? "checked" : ""}"></span>
            CERTIFICATION
          </div>
          <div class="certification-list">
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPatientRights ? "checked" : ""}"></span>
              I certify that I have received a copy and have been educated on the Patient's Rights and Responsibilities.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certOasisPrivacy ? "checked" : ""}"></span>
              I certify that I have received a copy and have been educated on the Patient's oasis privacy Rights and HIPPA regulations.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certHotlineInfo ? "checked" : ""}"></span>
              I certify that I have received the State and Joint Commission hotline information
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPrivacyRights ? "checked" : ""}"></span>
              I have been informed about the patient's privacy rights, and on how to file a complaint.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certPlanOfCare ? "checked" : ""}"></span>
              The Initial Plan of Care has been reviewed with me.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certRates ? "checked" : ""}"></span>
              The Home Health rates have been reviewed with me.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certTollFree ? "checked" : ""}"></span>
              I have been informed about the State and Joint Commission Home Health toll free hotline.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certEmergencyPlan ? "checked" : ""}"></span>
              I certify I have been advised and received written instructions regarding and Emergency Plan and Safety for my care.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certBrochure ? "checked" : ""}"></span>
              I have received a brochure about Infinite Home Health including the Non-Discriminatory Notice in the handbook.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certDocumentCopy ? "checked" : ""}"></span>
              I have received a copy of this document.
            </div>
            <div class="cert-item">
              <span class="consent-checkbox ${data.certAdvanceDirectives ? "checked" : ""}"></span>
              I have formulated Advance Directives with my legal advisor or my family.
              <span style="margin-left: 8px;">
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.advanceDirectivesYes ? "checked" : ""} disabled>
                  <span>Yes</span>
                </span>
                <span class="checkbox-inline">
                  <input type="checkbox" ${data.advanceDirectivesNo ? "checked" : ""} disabled>
                  <span>No</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div class="consent-section">
          <div class="consent-title">
            <span class="consent-checkbox ${data.consentPhotoRecords ? "checked" : ""}"></span>
            I AUTHORIZE INFINITE HOME HEALTH TO OBTAIN PHOTO RECORDS OF WOUND(S) DURING SERVICE.
          </div>
        </div>

        <div class="signature-section">
          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.patientSignature || "________________________________________________________________________________"}</div>
            </div>
          </div>
          <div class="signature-row">
            <div class="signature-field half">
              <div class="signature-label">Patient's Name:</div>
              <div class="signature-value">${data.patientNamePrinted || data.patientName || "________________________________________________________________________________"}</div>
            </div>
            <div class="signature-field half">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateUS(data.signatureDate || data.consentDate) || "________________"}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printConsentForm = (data) => {
    const printHTML = generateConsentPrintFormHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...consentFormData };

    // Save to consent history
    const historyEntry = {
      id: Date.now(),
      employeeName: consentFormData.employeeName,
      employeeId: consentFormData.employeeId,
      submittedAt: new Date().toISOString(),
      data: submitData
    };

    const updatedHistory = [historyEntry, ...consentHistory];
    setConsentHistory(updatedHistory);
    localStorage.setItem('consentHistory', JSON.stringify(updatedHistory));

    // Automatically trigger print, then reset form
    setTimeout(() => {
      printConsentForm(submitData);
      resetForm();
      setSelectedPosition("");
      setShowForm(false);
      setShowUserList(true);
    }, 300);
  };

  const resetForm = () => {
    setConsentFormData({
      employeeId: "",
      employeeName: "",
      patientName: "",
      patientDateOfBirth: "",
      patientAddress: "",
      patientPhone: "",
      consentDate: "",
      consentAdmission: false,
      consentReleaseInfo: false,
      consentCertification: false,
      consentBilling: false,
      consentInsurance: false,
      consentCMSAdvance: false,
      cmsPrivateInsurance: false,
      cmsSelfPay: false,
      consentPhotoRecords: false,
      visitFrequencySN: "",
      visitFrequencyCHHA: "",
      visitFrequencyPT: "",
      visitFrequencyOT: "",
      visitFrequencyST: "",
      visitFrequencyMSW: "",
      certPatientRights: false,
      certOasisPrivacy: false,
      certHotlineInfo: false,
      certPrivacyRights: false,
      certPlanOfCare: false,
      certRates: false,
      certTollFree: false,
      certEmergencyPlan: false,
      certBrochure: false,
      certDocumentCopy: false,
      certAdvanceDirectives: false,
      advanceDirectivesYes: false,
      advanceDirectivesNo: false,
      patientSignature: "",
      patientNamePrinted: "",
      signatureDate: "",
    });
    setSelectedPosition("");
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Patient Consent</h1>
        <button
          onClick={() => navigate("/forms")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          Back to Forms
        </button>
      </div>

      {showUserList && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Employee</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position *
            </label>
            <select
              id="selectedPosition"
              value={selectedPosition}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select a position...</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name *
            </label>
            <input
              type="text"
              name="employeeName"
              value={consentFormData.employeeName || ""}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter employee name"
              required
            />
          </div>
          <button
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!consentFormData.employeeName || consentFormData.employeeName.trim() === "") {
                alert("Please enter employee name");
                return;
              }
              setShowUserList(false);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            disabled={!selectedPosition || !consentFormData.employeeName || consentFormData.employeeName.trim() === ""}
          >
            Continue
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Information Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="employeeName"
                    value={consentFormData.employeeName || ""}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter employee name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Consent Sections */}
            <div className="border-t border-b border-gray-300 py-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Consent Sections</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setConsentFormData(prev => ({
                        ...prev,
                        consentAdmission: true,
                        consentReleaseInfo: true,
                        consentCertification: true,
                        consentBilling: true,
                        consentInsurance: true,
                        consentCMSAdvance: true,
                        consentPhotoRecords: true
                      }));
                    }}
                    className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConsentFormData(prev => ({
                        ...prev,
                        consentAdmission: false,
                        consentReleaseInfo: false,
                        consentCertification: false,
                        consentBilling: false,
                        consentInsurance: false,
                        consentCMSAdvance: false,
                        consentPhotoRecords: false
                      }));
                    }}
                    className="text-xs bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentAdmission"
                      checked={consentFormData.consentAdmission}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>REQUEST FOR ADMISSION AND CONSENT TO TREATMENT</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I consent to initial evaluation for eligibility and admission to services and treatment as ordered by my physician through Infinite Home Health. I understand that my care is directed and monitored by my physician and that Infinite Home Health is not liable for any acts or omissions when following the instructions of my physician, as physicians are neither employees nor agents of Infinite Home Health.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentReleaseInfo"
                      checked={consentFormData.consentReleaseInfo}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>AUTHORIZATION TO RELEASE INFORMATION</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I consent to the release of information from any hospital, skilled nursing facility, or home care agency where I have been a patient. I authorize health care professionals to disclose medical records to Infinite Home Health and authorize Infinite Home Health to share health status information with other health agencies, professionals involved in my care, or State, Federal, and other accrediting bodies for record review. I understand that I may request transfer to another HHA at any time.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentCertification"
                      checked={consentFormData.consentCertification}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>PATIENTS CERTIFICATION, AUTHORIZATION TO RELEASE INFORMATION, AND PAYMENT REQUEST</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I certify that the information given to Infinite Home Health for payment from CMS is true and correct. I authorize the release of all medical records allowed under HIPAA regulations and authorize payment on my behalf to Infinite Home Health.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentBilling"
                      checked={consentFormData.consentBilling}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>HOME HEALTH CONSOLIDATED BILLING UNDER PPS</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I understand that services subject to home health consolidated billing include SN care, CHHA, PT, ST, OT, MSW routine and non-routine medical supplies, and care for homebound patients involving cumbersome equipment. DME is excluded. I was advised in advance by the HHA about consolidated billing expectations from Medicare or other sources, including myself.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentInsurance"
                      checked={consentFormData.consentInsurance}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>ASSIGNMENT OF INSURANCE BENEFITS</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I authorize direct payment of insurance benefits to Infinite Home Health for services at a rate not exceeding regular charges. I understand that I am responsible for charges not covered by my insurance plan. Medicare covers all Home Health billable expenses.
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="consentCMSAdvance"
                      checked={consentFormData.consentCMSAdvance}
                      onChange={handleInputChange}
                      className="mt-1 mr-2"
                    />
                    <div>
                      <strong>CMS ADVANCE BENEFICIARY NOTICE</strong>
                      <p className="text-sm text-gray-700 mt-1">
                        I agree to individually obligate myself to pay Infinite Home Health for services according to regular rates and terms. If this account is referred to an attorney for collection, I will pay reasonable collection expenses, including attorney fees.
                      </p>
                      <div className="mt-2 flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="cmsPrivateInsurance"
                            checked={consentFormData.cmsPrivateInsurance}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 font-bold">PRIVATE INSURANCE</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="cmsSelfPay"
                            checked={consentFormData.cmsSelfPay}
                            onChange={handleInputChange}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 font-bold">SELF PAY</span>
                        </label>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Visit Frequency */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Visit Frequency</h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SN Visits:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencySN"
                    value={consentFormData.visitFrequencySN}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CHHA Visits:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencyCHHA"
                    value={consentFormData.visitFrequencyCHHA}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PT:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencyPT"
                    value={consentFormData.visitFrequencyPT}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OT:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencyOT"
                    value={consentFormData.visitFrequencyOT}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ST:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencyST"
                    value={consentFormData.visitFrequencyST}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MSW:
                  </label>
                  <input
                    type="text"
                    name="visitFrequencyMSW"
                    value={consentFormData.visitFrequencyMSW}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Certification Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={
                    consentFormData.certPatientRights &&
                    consentFormData.certOasisPrivacy &&
                    consentFormData.certHotlineInfo &&
                    consentFormData.certPrivacyRights &&
                    consentFormData.certPlanOfCare &&
                    consentFormData.certRates &&
                    consentFormData.certTollFree &&
                    consentFormData.certEmergencyPlan &&
                    consentFormData.certBrochure &&
                    consentFormData.certDocumentCopy &&
                    consentFormData.certAdvanceDirectives
                  }
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setConsentFormData(prev => ({
                      ...prev,
                      certPatientRights: isChecked,
                      certOasisPrivacy: isChecked,
                      certHotlineInfo: isChecked,
                      certPrivacyRights: isChecked,
                      certPlanOfCare: isChecked,
                      certRates: isChecked,
                      certTollFree: isChecked,
                      certEmergencyPlan: isChecked,
                      certBrochure: isChecked,
                      certDocumentCopy: isChecked,
                      certAdvanceDirectives: isChecked,
                      advanceDirectivesYes: isChecked ? prev.advanceDirectivesYes : false,
                      advanceDirectivesNo: isChecked ? prev.advanceDirectivesNo : false
                    }));
                  }}
                  className="mr-2"
                />
                <h3 className="text-lg font-semibold text-gray-800">Certification</h3>
              </div>

              <div className="space-y-2">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certPatientRights"
                    checked={consentFormData.certPatientRights}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I certify that I have received a copy and have been educated on the Patient's Rights and Responsibilities.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certOasisPrivacy"
                    checked={consentFormData.certOasisPrivacy}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I certify that I have received a copy and have been educated on the Patient's oasis privacy Rights and HIPPA regulations.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certHotlineInfo"
                    checked={consentFormData.certHotlineInfo}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I certify that I have received the State and Joint Commission hotline information</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certPrivacyRights"
                    checked={consentFormData.certPrivacyRights}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I have been informed about the patient's privacy rights, and on how to file a complaint.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certPlanOfCare"
                    checked={consentFormData.certPlanOfCare}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">The Initial Plan of Care has been reviewed with me.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certRates"
                    checked={consentFormData.certRates}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">The Home Health rates have been reviewed with me.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certTollFree"
                    checked={consentFormData.certTollFree}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I have been informed about the State and Joint Commission Home Health toll free hotline.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certEmergencyPlan"
                    checked={consentFormData.certEmergencyPlan}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I certify I have been advised and received written instructions regarding and Emergency Plan and Safety for my care.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certBrochure"
                    checked={consentFormData.certBrochure}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I have received a brochure about Infinite Home Health including the Non-Discriminatory Notice in the handbook.</span>
                </label>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="certDocumentCopy"
                    checked={consentFormData.certDocumentCopy}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm text-gray-700">I have received a copy of this document.</span>
                </label>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="certAdvanceDirectives"
                    checked={consentFormData.certAdvanceDirectives}
                    onChange={handleInputChange}
                    className="mt-1 mr-2"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-700">I have formulated Advance Directives with my legal advisor or my family.</span>
                    <div className="mt-2 flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="advanceDirectives"
                          checked={consentFormData.advanceDirectivesYes}
                          onChange={() => setConsentFormData(prev => ({
                            ...prev,
                            advanceDirectivesYes: true,
                            advanceDirectivesNo: false
                          }))}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="advanceDirectives"
                          checked={consentFormData.advanceDirectivesNo}
                          onChange={() => setConsentFormData(prev => ({
                            ...prev,
                            advanceDirectivesYes: false,
                            advanceDirectivesNo: true
                          }))}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Photo Records Authorization */}
            <div className="border-t border-b border-gray-300 py-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="consentPhotoRecords"
                  checked={consentFormData.consentPhotoRecords}
                  onChange={handleInputChange}
                  className="mt-1 mr-2"
                />
                <span className="text-sm font-semibold text-gray-800">
                  I AUTHORIZE INFINITE HOME HEALTH TO OBTAIN PHOTO RECORDS OF WOUND(S) DURING SERVICE.
                </span>
              </label>
            </div>

            {/* Signatures */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Signatures</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature:
                  </label>
                  <input
                    type="text"
                    name="patientSignature"
                    value={consentFormData.patientSignature}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patient's Name (Printed):
                    </label>
                    <input
                      type="text"
                      name="patientNamePrinted"
                      value={consentFormData.patientNamePrinted}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder={consentFormData.patientName || "Patient's full name"}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date:
                    </label>
                    <DateInput
                      name="signatureDate"
                      value={consentFormData.signatureDate}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t border-gray-300">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
              >
                Submit/Print
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setShowUserList(true);
                  resetForm();
                  setSelectedPosition("");
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PatientConsentForm;
